import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface UIContextType {
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  isIframeMode: boolean;
}

const UIContext = createContext<UIContextType | null>(null);

interface UIProviderProps {
  children: ReactNode;
}

export const UIProvider: React.FC<UIProviderProps> = ({ children }) => {
  // Check if we're in iframe mode by looking for the flag in localStorage
  // This flag is set during auto-login
  const [isIframeMode] = useState<boolean>(() => {
    return localStorage.getItem('iframeMode') === 'true';
  });

  // Default to collapsed if in iframe mode, otherwise expanded
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    if (saved !== null) {
      return saved === 'true';
    }
    // Default to collapsed in iframe mode
    return isIframeMode;
  });

  // Save sidebar state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isSidebarCollapsed.toString());
  }, [isSidebarCollapsed]);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(prev => !prev);
  };

  const setSidebarCollapsed = (collapsed: boolean) => {
    setIsSidebarCollapsed(collapsed);
  };

  const value: UIContextType = {
    isSidebarCollapsed,
    toggleSidebar,
    setSidebarCollapsed,
    isIframeMode
  };

  return (
    <UIContext.Provider value={value}>
      {children}
    </UIContext.Provider>
  );
};

export const useUI = (): UIContextType => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};

